import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { SessionDTO } from 'types/dto/login';
import ApiClient from 'services/apiClient';
import { REACT_APP_M_V1_META_SECURE } from 'constants/consts';

interface SessionContextType {
  session: SessionDTO | null;
  setSession: (session: SessionDTO | null) => void;
  clearSession: () => void;
  isSessionLoaded: boolean;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

const SecureStorage = {
  save: (key: string, data: SessionDTO) => {
    const encryptedData = btoa(encodeURIComponent(JSON.stringify(data)));
    sessionStorage.setItem(key, encryptedData);
  },
  load: (key: string): SessionDTO | null => {
    const rawData = sessionStorage.getItem(key);
    if (!rawData) return null;
    try {
      const decryptedData = decodeURIComponent(atob(rawData));
      return JSON.parse(decryptedData) as SessionDTO;
    } catch (e) {
      console.error('Failed to decrypt session data');
      return null;
    }
  }
};

export const SessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [session, setSessionState] = useState<SessionDTO | null>(() => {
    return SecureStorage.load(REACT_APP_M_V1_META_SECURE);
  });
  const [isSessionLoaded] = useState(true);

  useEffect(() => {
    if (session) {
      const apiClient = ApiClient.getInstance();
      apiClient.setSessionToken(session._tk);
    }
  }, []);

  const setSession = (newSession: SessionDTO | null) => {
    setSessionState(newSession);
    const apiClient = ApiClient.getInstance();

    if (newSession) {
      try {
        SecureStorage.save(REACT_APP_M_V1_META_SECURE, newSession);
        apiClient.setSessionToken(newSession._tk);
      } catch (error) {
        console.error('Failed to save session:', error);
      }
    } else {
      sessionStorage.removeItem(REACT_APP_M_V1_META_SECURE);
      apiClient.setSessionToken(null);
    }
  };

  const clearSession = () => {
    setSession(null);
  };

  const value: SessionContextType = {
    session,
    setSession,
    clearSession,
    isSessionLoaded
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = (): SessionContextType => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};